import React, { useEffect, useRef, useState } from 'react';

// Local types mirror ProteinDesignInput to avoid circular deps
export type MaskType = 'sequence' | 'structure' | 'sasa' | 'plddt';

export interface ResidueInfo {
  chain: string;
  resNum: number;
  resName: string;
  index: number;
}

export interface SequenceData {
  chain: string;
  sequence: string;
  residues: ResidueInfo[];
}

const MASK_COLORS: Record<MaskType, string> = {
  sequence: '#3B82F6',
  structure: '#EF4444',
  sasa: '#10B981',
  plddt: '#FACC15',
};

interface InputMaskerProps {
  sequences: SequenceData[];
  isReadOnly: boolean;
  activeMaskTypes: Set<MaskType>;
  maskedResidues: Map<MaskType, Set<string>>;
  onMaskedResiduesChange: (next: Map<MaskType, Set<string>>) => void;
}

export default function InputMasker({
  sequences,
  isReadOnly,
  activeMaskTypes,
  maskedResidues,
  onMaskedResiduesChange,
}: InputMaskerProps) {
  const [hoveredResidue, setHoveredResidue] = useState<ResidueInfo | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartResidue, setDragStartResidue] = useState<ResidueInfo | null>(null);
  const scrollContainerRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollIntervalRef = useRef<number | null>(null);
  const initialMasksRef = useRef<Map<MaskType, Set<string>>>(new Map());

  // Global mouseup to end drag selection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setDragStartResidue(null);
        // stop auto-scroll
        if (scrollIntervalRef.current) {
          cancelAnimationFrame(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      }
    };
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging]);

  const applyMasksToResidue = (residue: ResidueInfo) => {
    const key = `${residue.chain}-${residue.resNum}`;
    const newMasks = new Map<MaskType, Set<string>>();
    // copy
    maskedResidues.forEach((residues, maskType) => {
      newMasks.set(maskType, new Set(residues));
    });
    // remove from all
    const allTypes: MaskType[] = ['sequence', 'structure', 'sasa', 'plddt'];
    allTypes.forEach(mt => {
      const s = newMasks.get(mt) || new Set();
      s.delete(key);
      newMasks.set(mt, s);
    });
    // add for active
    activeMaskTypes.forEach(mt => {
      const s = newMasks.get(mt) || new Set();
      s.add(key);
      newMasks.set(mt, s);
    });
    onMaskedResiduesChange(newMasks);
  };

  const handleMouseDown = (residue: ResidueInfo) => {
    if (isReadOnly) return;
    setDragStartResidue(residue);
    // snapshot masks before drag
    const current = new Map<MaskType, Set<string>>();
    maskedResidues.forEach((res, mt) => current.set(mt, new Set(res)));
    initialMasksRef.current = current;
  };

  const handleMouseEnter = (residue: ResidueInfo, e: React.MouseEvent) => {
    setHoveredResidue(residue);
    if (!dragStartResidue) return;
    setIsDragging(true);

    const startIndex = dragStartResidue.index;
    const endIndex = residue.index;
    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);

    const newMasks = new Map<MaskType, Set<string>>();
    initialMasksRef.current.forEach((residues, mt) => newMasks.set(mt, new Set(residues)));

    const chainSeq = sequences.find(s => s.chain === residue.chain);
    if (chainSeq) {
      chainSeq.residues.forEach(res => {
        if (res.index >= minIndex && res.index <= maxIndex) {
          const key = `${res.chain}-${res.resNum}`;
          // remove from all
          (['sequence','structure','sasa','plddt'] as MaskType[]).forEach(mt => {
            const s = newMasks.get(mt) || new Set();
            s.delete(key);
            newMasks.set(mt, s);
          });
          // add for active
          activeMaskTypes.forEach(mt => {
            const s = newMasks.get(mt) || new Set();
            s.add(key);
            newMasks.set(mt, s);
          });
        }
      });
      onMaskedResiduesChange(newMasks);
    }

    // auto-scroll horizontally when dragging near edges
    const scrollContainer = scrollContainerRefs.current.get(residue.chain);
    if (scrollContainer) {
      const rect = scrollContainer.getBoundingClientRect();
      const mouseX = e.clientX;
      const zone = 60; // px from edge
      const speed = 10; // px/frame
      if (mouseX - rect.left < zone) {
        const step = () => {
          scrollContainer.scrollLeft = Math.max(0, scrollContainer.scrollLeft - speed);
          scrollIntervalRef.current = requestAnimationFrame(step) as unknown as number;
        };
        if (!scrollIntervalRef.current) step();
      } else if (rect.right - mouseX < zone) {
        const step = () => {
          scrollContainer.scrollLeft = scrollContainer.scrollLeft + speed;
          scrollIntervalRef.current = requestAnimationFrame(step) as unknown as number;
        };
        if (!scrollIntervalRef.current) step();
      } else {
        if (scrollIntervalRef.current) {
          cancelAnimationFrame(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStartResidue(null);
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
      <style>{`
        .sequence-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .sequence-scroll::-webkit-scrollbar { height: 0px; }
      `}</style>

      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-3xl font-light text-gray-900">Sequence View</h2>
        {hoveredResidue && (
          <div className="text-sm text-gray-600">
            <span>Current Residue: </span>
            <span className="font-semibold">Chain {hoveredResidue.chain} Pos {hoveredResidue.resNum}</span>
            {(() => {
              const key = `${hoveredResidue.chain}-${hoveredResidue.resNum}`;
              const applied: MaskType[] = [];
              (['sequence','structure','sasa','plddt'] as MaskType[]).forEach(mt => {
                if (maskedResidues.get(mt)?.has(key)) applied.push(mt);
              });
              return applied.length > 0
                ? (<><span className="ml-2">Masks applied: </span>{applied.map((mt,i) => (
                    <span key={mt}><span className="font-semibold" style={{color: MASK_COLORS[mt]}}>{mt.toUpperCase()}</span>{i<applied.length-1 && <span>, </span>}</span>
                  ))}</>)
                : (<span className="ml-2 text-gray-500">No masks applied</span>);
            })()}
          </div>
        )}
      </div>

      {/* Scrollable sequences */}
      <div className="h-[110px] overflow-y-auto" onMouseLeave={() => setHoveredResidue(null)}>
        {sequences.map(seq => (
          <div key={seq.chain} className="mb-3">
            <div className="text-base text-gray-500 mb-1">Chain {seq.chain}</div>
            <div className="relative overflow-hidden pb-1">
              <div
                className="sequence-scroll"
                ref={(el) => { if (el) scrollContainerRefs.current.set(seq.chain, el); }}
              >
                <div className="font-mono text-lg select-none inline-block pr-4 sequence-container">
                  {seq.residues.map((residue, idx) => {
                    const key = `${residue.chain}-${residue.resNum}`;
                    const active: MaskType[] = [];
                    (['sequence','structure','sasa','plddt'] as MaskType[]).forEach(mt => {
                      if (maskedResidues.get(mt)?.has(key)) active.push(mt);
                    });
                    return (
                      <span key={key}>
                        <span
                          className={`inline-block px-1.5 pb-0 select-none relative mask-indicator ${isReadOnly ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'}`}
                          onMouseDown={(e) => { e.preventDefault(); if (!isReadOnly) handleMouseDown(residue); }}
                          onMouseEnter={(e) => handleMouseEnter(residue, e)}
                          onMouseUp={handleMouseUp}
                          onClick={() => { if (!isReadOnly && !isDragging) applyMasksToResidue(residue); }}
                          title={`${residue.resName} ${residue.resNum}`}
                        >
                          {residue.resName}
                          {active.map(mt => (
                            <div key={mt} style={{ position:'absolute', top:`calc(100% - 4px + ${({sequence:0,structure:3,sasa:6,plddt:9} as Record<MaskType,number>)[mt]}px)`, left:'0', right:'0', height:'3px', backgroundColor: MASK_COLORS[mt] }} />
                          ))}
                        </span>
                        {(idx + 1) % 10 === 0 && (
                          <span className="text-base text-gray-400 ml-0.5 mr-1">{residue.resNum}</span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

